const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export type SharePermission = 'viewer' | 'editor';

export interface ShareInfo {
  id: string;
  shareType: 'individual' | 'group';
  sharedWith: {
    type: 'user' | 'group';
    id: string;
    name: string;
    email?: string;
  };
  permission: SharePermission;
  createdAt: string;
}

export interface ShareTarget {
  type: 'user' | 'group';
  id: string;
  permission?: SharePermission;
}

export interface ShareResult {
  shared: Array<{ type: string; id: string; name: string; permission: SharePermission }>;
  alreadyShared: Array<{ type: string; id: string }>;
  notFound: Array<{ type: string; id: string }>;
}

class SharingService {
  private getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
    };
  }

  // Get all shares for a customer
  async getCustomerShares(customerId: string): Promise<ShareInfo[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/shares`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer shares');
      }

      const data = await response.json();
      return data.shares;
    } catch (error) {
      console.error('Error fetching customer shares:', error);
      throw error;
    }
  }

  // Share a customer with users or groups
  async shareCustomer(customerId: string, shares: ShareTarget[]): Promise<ShareResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/share`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ shares })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to share customer' }));
        throw new Error(errorData.error || 'Failed to share customer');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sharing customer:', error);
      throw error;
    }
  }

  // Update permission on an existing share
  async updateSharePermission(customerId: string, shareId: string, permission: SharePermission): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/share/${shareId}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ permission })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update share permission' }));
        throw new Error(errorData.error || 'Failed to update share permission');
      }
    } catch (error) {
      console.error('Error updating share permission:', error);
      throw error;
    }
  }

  // Revoke a share
  async revokeShare(customerId: string, shareId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/share/${shareId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to revoke share' }));
        throw new Error(errorData.error || 'Failed to revoke share');
      }
    } catch (error) {
      console.error('Error revoking share:', error);
      throw error;
    }
  }
}

export const sharingService = new SharingService();
